import { queryClient } from "@/lib/queryClient";

const API_BASE = "/pyapi/admin";

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

type FetchOptions = {
  method?: HttpMethod;
  body?: unknown;
  csrfToken?: string | null;
  signal?: AbortSignal;
};

async function adminRequest<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (options.csrfToken) {
    headers["x-admin-csrf"] = options.csrfToken;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method: options.method ?? "GET",
    credentials: "include",
    headers,
    signal: options.signal,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  if (response.status === 204) {
    return undefined as T;
  }

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Request failed with ${response.status}`);
  }

  return (await response.json()) as T;
}

export type AdminSessionStatus = {
  authenticated: boolean;
  username?: string | null;
  issuedAt?: string | null;
  csrfToken?: string | null;
};

export type AdminUser = {
  id: string;
  provider: string;
  emailHash?: string | null;
  displayName?: string | null;
  avatarOriginalUrl?: string | null;
  avatarCachedUrl?: string | null;
  status: string;
  disabledReason?: string | null;
  disabledAt?: string | null;
  deletedAt?: string | null;
  deletedReason?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  lastLoginAt?: string | null;
  lastActiveAt?: string | null;
  verificationState?: string | null;
  charactersCount: number;
  mfaEnrolled: boolean;
  apiKeysConfigured: boolean;
  recoveryPending: boolean;
  recoveryRequestedAt?: string | null;
};

export type AdminUserList = {
  items: AdminUser[];
  total: number | null;
  page: number;
  pageSize: number;
};

export type AdminQueueNote = {
  actor: string;
  message: string;
  createdAt: string;
};

export type AdminQueueEntry = {
  id: string;
  subjectId?: string | null;
  subjectType: string;
  category: string;
  severity: string;
  status: string;
  submittedBy?: string | null;
  assignedTo?: string | null;
  createdAt: string;
  updatedAt: string;
  metadata: Record<string, unknown>;
  notes: AdminQueueNote[];
  resolutionNote?: string | null;
};

export type AdminQueueList = {
  items: AdminQueueEntry[];
  total: number | null;
  page: number;
  pageSize: number;
};

/** Content rating controls visibility and filtering */
export type ContentRating = "general" | "mature" | "explicit";

/** Generation mode controls which AI prompt templates are used */
export type GenerationMode = "sfw" | "nsfw";

export type AdminCharacter = {
  id: string;
  name?: string | null;
  ownerId?: string | null;
  ownerDisplayName?: string | null;
  visibility?: string | null;
  /** @deprecated Use contentRating instead */
  nsfw: boolean;
  /** New field: controls which AI prompt templates are used */
  generationMode?: GenerationMode;
  /** New field: controls visibility/filtering (general/mature/explicit) */
  contentRating?: ContentRating;
  /** Admin moderation overrides */
  adminFlags?: { nsfw?: boolean; updatedAt?: string | null; updatedBy?: string | null };
  shareToken?: string | null;
  shareTokenExpiresAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  // Deleted status fields - used to match browse API public filter logic
  status?: string | null;
  deleted?: boolean;
  deletedAt?: string | null;
  deleted_at?: string | null;
  tags: string[];
  scenarioTags?: string[];
  imageFlags: Record<string, { nsfw?: boolean; updatedAt?: string | null }>;
  images: Record<string, string>;
  /** Metadata for images (label, prompt, provider, model, etc.) */
  imageMetadata?: Record<string, { label?: string; prompt?: string; provider?: string; model?: string; referenceImageId?: string }>;
  /** Image types that exist in storage but are not referenced by the character */
  orphanedImageTypes?: string[];
};

export type AdminCharacterList = {
  items: AdminCharacter[];
};

export type AdminCharacterImageRecord = {
  imageType: string;
  url?: string | null;
  storagePath?: string | null;
  prompt?: string | null;
  provider?: string | null;
  model?: string | null;
  label?: string | null;
  referenceImageId?: string | null;
  updatedAt?: string | null;
  external: boolean;
  orphaned: boolean;
};

export type AdminCharacterImageList = {
  items: AdminCharacterImageRecord[];
};

export type AdminOrphanedImageRecord = {
  characterId: string;
  characterName?: string | null;
  ownerId?: string | null;
  ownerDisplayName?: string | null;
  ownerStatus?: string | null;
  ownerLastLoginAt?: string | null;
  ownerDeletedAt?: string | null;
  image: AdminCharacterImageRecord;
};

export type AdminOrphanedImageList = {
  items: AdminOrphanedImageRecord[];
};

export type AdminSearchResult = {
  users: AdminUser[];
  characters: AdminCharacter[];
  queue: AdminQueueEntry[];
};

export type AdminDashboard = {
  users: number;
  activeUsers: number;
  pendingQueue: number;
  inProgressQueue: number;
  resolvedQueue: number;
  nsfwCharacters: number;
};

export type AdminAIGatewayConfigResponse = {
  config: Record<string, any>;
};

export type AdminAIKeyMetadata = {
  id: string;
  provider: string;
  displayName: string;
  createdAt?: string | null;
  updatedAt?: string | null;
  lastUsedAt?: string | null;
  meta: Record<string, any>;
};

export type AdminAIKeyListResponse = {
  items: AdminAIKeyMetadata[];
};

export type AdminAITestResponse = {
  success: boolean;
  capability: "text" | "image";
  provider?: string | null;
  model?: string | null;
  durationMs?: number | null;
  attempts?: number | null;
  content?: string | null;
  imageData?: string | null;
  usage?: Record<string, any> | null;
  error?: string | null;
};

export type AdminAITestRequest = {
  capability: "text" | "image";
  provider?: string | null;
  credentialId?: string | null;
  model?: string | null;
  allowFallback?: boolean | null;
  prompt?: string | null;
  messages?: Array<{ role: string; content: string }> | null;
  options?: Record<string, any> | null;
};

export const adminApi = {
  createSession: (username: string, password: string) =>
    adminRequest<{
      username: string;
      issuedAt: string;
      expiresInSeconds: number;
      csrfToken: string;
    }>("/session", {
      method: "POST",
      body: { username, password },
    }),

  readSession: () => adminRequest<AdminSessionStatus>("/session"),

  deleteSession: (csrfToken: string) =>
    adminRequest<void>("/session", { method: "DELETE", csrfToken }),

  listUsers: (params: {
    page: number;
    pageSize: number;
    sort: string;
    direction: string;
    status?: string | null;
    search?: string | null;
  }) => {
    const query = new URLSearchParams({
      page: String(params.page),
      pageSize: String(params.pageSize),
      sort: params.sort,
      direction: params.direction,
    });
    if (params.status) query.set("status", params.status);
    if (params.search) query.set("search", params.search);
    return adminRequest<AdminUserList>(`/users?${query.toString()}`);
  },

  disableUser: (userId: string, reason: string | null, csrfToken: string) =>
    adminRequest<AdminUser>(`/users/${userId}/disable`, {
      method: "POST",
      csrfToken,
      body: { reason },
    }),

  enableUser: (userId: string, csrfToken: string) =>
    adminRequest<AdminUser>(`/users/${userId}/enable`, {
      method: "POST",
      csrfToken,
    }),

  triggerRecovery: (userId: string, csrfToken: string) =>
    adminRequest<AdminUser>(`/users/${userId}/recovery`, {
      method: "POST",
      csrfToken,
    }),

  restoreUser: (userId: string, csrfToken: string) =>
    adminRequest<AdminUser>(`/users/${userId}/restore`, {
      method: "POST",
      csrfToken,
    }),

  deleteUser: (userId: string, csrfToken: string) =>
    adminRequest<AdminUser>(`/users/${userId}`, {
      method: "DELETE",
      csrfToken,
    }),

  listQueue: (params: {
    page: number;
    pageSize: number;
    sort: string;
    direction: string;
    status?: string | null;
    search?: string | null;
  }) => {
    const query = new URLSearchParams({
      page: String(params.page),
      pageSize: String(params.pageSize),
      sort: params.sort,
      direction: params.direction,
    });
    if (params.status) query.set("status", params.status);
    if (params.search) query.set("search", params.search);
    return adminRequest<AdminQueueList>(`/queue?${query.toString()}`);
  },

  updateQueueEntry: (
    entryId: string,
    body: { status?: string | null; assignedTo?: string | null; note?: string | null; resolutionNote?: string | null },
    csrfToken: string,
  ) =>
    adminRequest<AdminQueueEntry>(`/queue/${entryId}`, {
      method: "POST",
      csrfToken,
      body,
    }),

  bulkQueueAction: (entryIds: string[], status: string, note: string | null, csrfToken: string) =>
    adminRequest<AdminQueueEntry[]>(`/queue/bulk`, {
      method: "POST",
      csrfToken,
      body: { entryIds, status, note },
    }),

  listCharacters: (params: { ownerId?: string | null; search?: string | null; limit: number; offset?: number; orphanedOnly?: boolean; publicOnly?: boolean }) => {
    const query = new URLSearchParams({ limit: String(params.limit) });
    if (params.offset) query.set("offset", String(params.offset));
    if (params.ownerId) query.set("ownerId", params.ownerId);
    if (params.search) query.set("search", params.search);
    if (params.orphanedOnly) query.set("orphanedOnly", "true");
    if (params.publicOnly) query.set("publicOnly", "true");
    return adminRequest<AdminCharacterList>(`/characters?${query.toString()}`);
  },

  listCharacterImages: (charId: string) =>
    adminRequest<AdminCharacterImageList>(`/characters/${charId}/images`),

  getCharacter: (charId: string) => adminRequest<AdminCharacter>(`/characters/${charId}`),

  /** @deprecated Use setCharacterContentRating instead */
  setCharacterNsfw: (charId: string, nsfw: boolean, csrfToken: string) =>
    adminRequest<AdminCharacter>(`/characters/${charId}/nsfw`, {
      method: "POST",
      csrfToken,
      body: { nsfw },
    }),

  /** Set character content rating (general/mature/explicit) */
  setCharacterContentRating: (charId: string, contentRating: ContentRating, csrfToken: string) =>
    adminRequest<AdminCharacter>(`/characters/${charId}/content-rating`, {
      method: "POST",
      csrfToken,
      body: { contentRating },
    }),

  setImageNsfw: (charId: string, imageType: string, nsfw: boolean, csrfToken: string) =>
    adminRequest<AdminCharacter>(`/characters/${charId}/images/${encodeURIComponent(imageType)}/nsfw`, {
      method: "POST",
      csrfToken,
      body: { nsfw },
    }),

  deleteCharacterImage: (charId: string, imageType: string, csrfToken: string) =>
    adminRequest<void>(`/characters/${charId}/images/${encodeURIComponent(imageType)}`, {
      method: "DELETE",
      csrfToken,
    }),

  revokeShare: (charId: string, csrfToken: string) =>
    adminRequest<AdminCharacter>(`/characters/${charId}/revoke-share`, {
      method: "POST",
      csrfToken,
    }),

  setCharacterVisibility: (charId: string, visibility: string, csrfToken: string) =>
    adminRequest<AdminCharacter>(`/characters/${charId}/visibility`, {
      method: "POST",
      csrfToken,
      body: { visibility },
    }),

  updateScenarioTags: (charId: string, scenarioTags: string[], csrfToken: string) =>
    adminRequest<AdminCharacter>(`/characters/${charId}/scenario-tags`, {
      method: "POST",
      csrfToken,
      body: { scenarioTags },
    }),

  deleteCharacter: (charId: string, csrfToken: string) =>
    adminRequest<void>(`/characters/${charId}`, {
      method: "DELETE",
      csrfToken,
    }),

  listOrphanedImages: (limit = 500) =>
    adminRequest<AdminOrphanedImageList>(`/images/orphaned?${new URLSearchParams({ limit: String(limit) }).toString()}`),

  cleanupOrphanedImages: (limit: number, csrfToken: string) =>
    adminRequest<{ total: number; deleted: number; failed: number; details: any[] }>(
      `/images/orphaned/cleanup?${new URLSearchParams({ limit: String(limit) }).toString()}`,
      {
        method: "POST",
        csrfToken,
      }
    ),

  cleanupCharacterImageMap: (charId: string, csrfToken: string) =>
    adminRequest<{ characterId: string; removed: string[]; count: number }>(`/characters/${charId}/images/cleanup-map`, {
      method: "POST",
      csrfToken,
    }),

  listUnreferencedStorageFiles: (charId: string, limit: number = 100) =>
    adminRequest<{ items: any[]; total: number }>(`/characters/${charId}/images/unreferenced?limit=${limit}`),

  cleanupUnreferencedStorageFiles: (charId: string, csrfToken: string, limit: number = 100) =>
    adminRequest<{ characterId: string; total: number; deleted: number; failed: number; details: any[] }>(
      `/characters/${charId}/images/cleanup-unreferenced?limit=${limit}`,
      {
        method: "POST",
        csrfToken,
      }
    ),

  /** List all orphaned images by type (subcollection, storage, character_map) */
  listAllOrphanedImages: (limit = 500) =>
    adminRequest<{
      subcollection_orphans: Array<{
        characterId: string;
        characterName?: string | null;
        ownerId?: string | null;
        imageType: string;
        storagePath?: string | null;
        url?: string | null;
        orphanType: string;
      }>;
      storage_orphans: Array<{
        storagePath: string;
        characterId: string;
        imageType: string;
        size?: number | null;
        updated?: string | null;
        orphanType: string;
      }>;
      character_map_orphans: Array<{
        characterId: string;
        characterName?: string | null;
        ownerId?: string | null;
        imageType: string;
        url: string;
        orphanType: string;
      }>;
    }>(`/images/all-orphaned?${new URLSearchParams({ limit: String(limit) }).toString()}`),

  /** Comprehensive cleanup across all storage layers with dry-run support */
  fullImageCleanup: (dryRun: boolean, limit: number, csrfToken: string) =>
    adminRequest<{
      dry_run: boolean;
      subcollection: { found: number; deleted: number; failed: number; items: any[] };
      storage: { found: number; deleted: number; failed: number; items: any[] };
      character_map: { found: number; deleted: number; failed: number; items: any[] };
      total_found: number;
      total_deleted: number;
    }>(
      `/images/full-cleanup?${new URLSearchParams({ dry_run: String(dryRun), limit: String(limit) }).toString()}`,
      {
        method: "POST",
        csrfToken,
      }
    ),

  globalSearch: (query: string) => adminRequest<AdminSearchResult>(`/search?${new URLSearchParams({ query }).toString()}`),

  dashboard: () => adminRequest<AdminDashboard>(`/dashboard`),

  readAIGatewayConfig: () => adminRequest<AdminAIGatewayConfigResponse>("/ai/config"),

  updateAIGatewayConfig: (config: Record<string, any>, csrfToken: string) =>
    adminRequest<AdminAIGatewayConfigResponse>("/ai/config", {
      method: "PUT",
      csrfToken,
      body: { config },
    }),

  listAIKeys: () => adminRequest<AdminAIKeyListResponse>("/ai/keys"),

  getAIKey: (keyId: string) => adminRequest<AdminAIKeyMetadata>(`/ai/keys/${encodeURIComponent(keyId)}`),

  createAIKey: (
    payload: { id: string; provider: string; displayName: string; secret: string; meta?: Record<string, any> | null },
    csrfToken: string,
  ) =>
    adminRequest<AdminAIKeyMetadata>("/ai/keys", {
      method: "POST",
      csrfToken,
      body: {
        id: payload.id,
        provider: payload.provider,
        displayName: payload.displayName,
        secret: payload.secret,
        meta: payload.meta ?? {},
      },
    }),

  updateAIKey: (
    keyId: string,
    payload: { provider?: string | null; displayName?: string | null; secret?: string | null; meta?: Record<string, any> | null },
    csrfToken: string,
  ) =>
    adminRequest<AdminAIKeyMetadata>(`/ai/keys/${encodeURIComponent(keyId)}`, {
      method: "PUT",
      csrfToken,
      body: {
        provider: payload.provider ?? undefined,
        displayName: payload.displayName ?? undefined,
        secret: payload.secret ?? undefined,
        meta: payload.meta ?? undefined,
      },
    }),

  deleteAIKey: (keyId: string, csrfToken: string) =>
    adminRequest<void>(`/ai/keys/${encodeURIComponent(keyId)}`, {
      method: "DELETE",
      csrfToken,
    }),

  testAIGateway: (payload: AdminAITestRequest, csrfToken: string) =>
    adminRequest<AdminAITestResponse>("/ai/config/test", {
      method: "POST",
      csrfToken,
      body: payload,
    }),
};

export function invalidateAdminQueries(pattern: string) {
  queryClient.invalidateQueries({ queryKey: [pattern] });
}

// ============================================================================
// AI Metrics Types and API
// ============================================================================

export type MetricsSummaryByProvider = {
  provider: string;
  total: number;
  success: number;
  failed: number;
  success_rate: number;
  avg_duration_ms: number;
  p50_duration_ms?: number | null;
  p95_duration_ms?: number | null;
  p99_duration_ms?: number | null;
};

export type MetricsSummaryByRequestType = {
  request_type: string;
  total: number;
  success: number;
  failed: number;
  success_rate: number;
  avg_duration_ms: number;
};

export type MetricsSummaryByErrorCategory = {
  error_category: string;
  count: number;
  percentage: number;
};

export type MetricsSummaryByModel = {
  model: string;
  provider: string;
  total: number;
  success: number;
  failed: number;
  success_rate: number;
  avg_duration_ms: number;
};

export type MetricsSummary = {
  totalRequests: number;
  successCount: number;
  failedCount: number;
  successRate: number;
  avgDurationMs: number;
  p50DurationMs?: number | null;
  p95DurationMs?: number | null;
  p99DurationMs?: number | null;
  byProvider: MetricsSummaryByProvider[];
  byRequestType: MetricsSummaryByRequestType[];
  byErrorCategory: MetricsSummaryByErrorCategory[];
  byModel: MetricsSummaryByModel[];
  start: string;
  end: string;
};

export type TimeSeriesBucket = {
  timestamp: string;
  total: number;
  success: number;
  failed: number;
  avgDurationMs: number;
  p50DurationMs?: number | null;
  p95DurationMs?: number | null;
  p99DurationMs?: number | null;
};

export type TimeSeriesGroupBucket = {
  timestamp: string;
  groups: Record<string, number>;
};

export type TimeSeriesResponse = {
  buckets: TimeSeriesBucket[];
  interval: string;
  start: string;
  end: string;
  groupBy?: string | null;
  groupedBuckets?: TimeSeriesGroupBucket[] | null;
};

export type ErrorRecord = {
  id: string;
  timestamp: string;
  provider: string;
  model: string;
  requestType: string;
  errorCategory: string;
  errorCode?: string | null;
  errorMessage?: string | null;
  statusCode?: number | null;
  durationMs: number;
  userId?: string | null;
  requestId?: string | null;
};

export type ErrorsResponse = {
  errors: ErrorRecord[];
  total: number;
  start: string;
  end: string;
};

export type ProviderHealthRecord = {
  provider: string;
  totalRequests: number;
  successRate: number;
  avgDurationMs: number;
  p50DurationMs?: number | null;
  p95DurationMs?: number | null;
  p99DurationMs?: number | null;
  errorBreakdown: Record<string, number>;
  lastErrorAt?: string | null;
  lastSuccessAt?: string | null;
};

export type ProviderHealthResponse = {
  providers: ProviderHealthRecord[];
  start: string;
  end: string;
};

export const adminMetricsApi = {
  getSummary: (params?: { start?: string; end?: string; provider?: string; requestType?: string }) => {
    const query = new URLSearchParams();
    if (params?.start) query.set("start", params.start);
    if (params?.end) query.set("end", params.end);
    if (params?.provider) query.set("provider", params.provider);
    if (params?.requestType) query.set("requestType", params.requestType);
    const qs = query.toString();
    return adminRequest<MetricsSummary>(`/metrics/summary${qs ? `?${qs}` : ""}`);
  },

  getTimeseries: (params?: { start?: string; end?: string; interval?: string; groupBy?: string }) => {
    const query = new URLSearchParams();
    if (params?.start) query.set("start", params.start);
    if (params?.end) query.set("end", params.end);
    if (params?.interval) query.set("interval", params.interval);
    if (params?.groupBy) query.set("groupBy", params.groupBy);
    const qs = query.toString();
    return adminRequest<TimeSeriesResponse>(`/metrics/timeseries${qs ? `?${qs}` : ""}`);
  },

  getErrors: (params?: { start?: string; end?: string; provider?: string; errorCategory?: string; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.start) query.set("start", params.start);
    if (params?.end) query.set("end", params.end);
    if (params?.provider) query.set("provider", params.provider);
    if (params?.errorCategory) query.set("errorCategory", params.errorCategory);
    if (params?.limit) query.set("limit", String(params.limit));
    const qs = query.toString();
    return adminRequest<ErrorsResponse>(`/metrics/errors${qs ? `?${qs}` : ""}`);
  },

  getProviderHealth: (params?: { start?: string; end?: string }) => {
    const query = new URLSearchParams();
    if (params?.start) query.set("start", params.start);
    if (params?.end) query.set("end", params.end);
    const qs = query.toString();
    return adminRequest<ProviderHealthResponse>(`/metrics/providers${qs ? `?${qs}` : ""}`);
  },
};
